import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs, { setLocale, setTimezone } from "./locale-dayjs";
import i18n from "./internationalization";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(name: string) {
  if (!name) return "";
  if (name.length === 1) return name;
  if (name.indexOf(" ") === -1) return name[0];
  const [firstName, lastName] = name.split(" ");
  return `${firstName[0]}${lastName[0]}`;
}

// Helper functions for localStorage
export const getStoredValue = (key: string, defaultValue: string) => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(key);
    return stored !== null ? stored : defaultValue;
  }
  return defaultValue;
};

export const setStoredValue = (key: string, value: string) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(key, value);
  }
};

// Get device language
export const getDeviceLanguage = () => {
  return getStoredValue("i18nextLng", "en");
};

// Get device timezone
export const getDeviceTimezone = () => {
  return typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "America/New_York";
};

export const fetchUserSettings = async () => {
  const language = getStoredValue("language", getDeviceLanguage());
  const timezone = getStoredValue("timezone", getDeviceTimezone());
  setLocale(language);
  setTimezone(timezone);
  i18n.changeLanguage(language);
  return {
    language: getStoredValue("language", getDeviceLanguage()),
    timezone: getStoredValue("timezone", getDeviceTimezone()),
  };
};

export const formatDate = (dateString: string): string => {
  const timezone = getStoredValue("timezone", getDeviceTimezone());
  const locale = getStoredValue("language", getDeviceLanguage());

  return dayjs(dateString).locale(locale).tz(timezone).format("MM/DD/YYYY");
};

export const formatTime = (dateString: string): string => {
  const timezone = getStoredValue("timezone", getDeviceTimezone());
  const locale = getStoredValue("language", getDeviceLanguage());

  return dayjs(dateString).locale(locale).tz(timezone).format(
    "MM/DD/YY hh:mm A",
  );
};

export const formatTimeFromNow = (dateString: string): string => {
  const timezone = getStoredValue("timezone", getDeviceTimezone());
  const locale = getStoredValue("language", getDeviceLanguage());

  return dayjs(dateString).locale(locale).tz(timezone).fromNow();
};

export const formatHumanDatetime = (dateString: string): string => {
  const timezone = getStoredValue("timezone", getDeviceTimezone());
  const locale = getStoredValue("language", getDeviceLanguage());

  return dayjs(dateString).locale(locale).tz(timezone).format(
    "MMMM DD, YYYY hh:mm A",
  );
};

// example use
//toastMessage(toast, 'SUCCESS', 'Message sent successfully');
export const toastMessage = (
  toast: (params: {
    variant: "destructive" | "default";
    duration: number;
    title: string;
    description: string;
  }) => void,
  title: string,
  description: string = "",
  success: boolean = true,
) => {
  const variant: "destructive" | "default" = success
    ? "default"
    : "destructive";

  toast({
    variant,
    duration: variant === "destructive" ? 5000 : 3000,
    title,
    description,
  });
};

export type Address = {
  street: string;
  city: string;
  state: string;
  zip_code: string;
};

const formatAddressPart = (part: string | undefined) =>
  part ? encodeURIComponent(part.trim()) : "";
export const formatAddressUrl = ({
  street,
  city,
  state,
  zip_code,
}: Address): string => {
  const addressParts = [
    street && formatAddressPart(street),
    city && formatAddressPart(city),
    state && formatAddressPart(state),
    zip_code && formatAddressPart(zip_code),
  ].filter(Boolean);

  const address = addressParts.join(",+").replace(/\++/g, "+");
  return `https://www.google.com/maps/place/${address}`;
};

